import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserContent } from '../../services/contentService';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [recentPosts, setRecentPosts] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, userProfile, signOut, subscription } = useAuth();
  const tier = subscription?.tier || 'free';
  const isPro = tier === 'pro' || tier === 'enterprise';
  const profileMenuRef = useRef(null);

  // Close profile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  // Fetch recent posts for sidebar
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const loadRecent = async () => {
      try {
        const posts = await getUserContent();
        if (cancelled) return;
        // Deduplicate by original idea (since getUserContent splits per platform)
        const seen = new Set();
        const unique = [];
        for (const post of posts) {
          // Extract base ID (remove _linkedin / _twitter suffix)
          const baseId = post.id?.replace(/_linkedin$|_twitter$/, '');
          if (!seen.has(baseId)) {
            seen.add(baseId);
            unique.push({ ...post, baseId });
          }
          if (unique.length >= 5) break;
        }
        setRecentPosts(unique);
      } catch { /* ignore */ }
    };
    loadRecent();
    return () => { cancelled = true; };
  }, [user]);

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Schedule', path: '/schedule', icon: 'Calendar' },
    { label: 'Engagement', path: '/engagement-tracker-trending-alerts', icon: 'Bell', comingSoon: true },
    { label: 'Writing Styles', path: '/writing-styles-templates', icon: 'Sparkles' },
    { label: 'Link Accounts', path: '/o-auth-integration-setup', icon: 'Link' },
  ];

  const isActive = (path) => {
    if (path === '/idea-refiner') return location?.pathname === '/idea-refiner';
    return location?.pathname === path;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleNewPost = () => {
    navigate('/idea-refiner', { state: { newSession: true } });
  };

  return (
    <>
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {isCollapsed ? (
            /* Collapsed: logo icon doubles as expand button */
            <button
              onClick={() => setIsCollapsed(false)}
              className="sidebar-logo-icon cursor-pointer hover:opacity-80 transition-opacity"
              title="Expand sidebar"
            >
              <Icon name="Send" size={20} className="text-white" />
            </button>
          ) : (
            <>
              <Link to="/dashboard" className="sidebar-logo">
                <div className="sidebar-logo-icon">
                  <Icon name="Send" size={20} className="text-white" />
                </div>
                <span className="sidebar-logo-text">POSTRequest</span>
              </Link>
              <button
                className="sidebar-toggle"
                onClick={() => setIsCollapsed(true)}
                aria-label="Collapse sidebar"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
            </>
          )}
        </div>

        <nav className="sidebar-nav">
          {/* New Post Button — prominent, Gmail Compose-like */}
          {isCollapsed ? (
            <div className="new-post-glow mx-auto mb-3" style={{ width: 'fit-content' }}>
              <button
                onClick={handleNewPost}
                className="w-10 h-10 rounded-[12.5px] bg-background flex items-center justify-center hover:bg-muted transition-all"
                title="New Post"
              >
                <Icon name="Plus" size={20} className="text-foreground" />
              </button>
            </div>
          ) : (
            <div className="new-post-glow mb-4">
              <button
                onClick={handleNewPost}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-[12.5px] text-sm font-semibold text-foreground bg-background hover:bg-muted transition-all"
              >
                <Icon name="Plus" size={20} />
                <span>New Post</span>
              </button>
            </div>
          )}

          {navigationItems?.map((item) => (
            item?.comingSoon ? (
              <div
                key={item?.path}
                className="sidebar-nav-link opacity-50 cursor-not-allowed relative"
                title={isCollapsed ? `${item?.label} - Coming Soon` : ''}
              >
                <Icon name={item?.icon} size={20} />
                {!isCollapsed && (
                  <>
                    <span>{item?.label}</span>
                    <span className="ml-auto text-[9px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Soon</span>
                  </>
                )}
              </div>
            ) : (
              <Link
                key={item?.path}
                to={item?.path}
                className={`sidebar-nav-link ${isActive(item?.path) ? 'active' : ''}`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon name={item?.icon} size={20} />
                {!isCollapsed && <span>{item?.label}</span>}
              </Link>
            )
          ))}

          {/* Recent Posts Section */}
          {!isCollapsed && recentPosts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recent Posts</p>
              {recentPosts.map((post) => (
                <Link
                  key={post.baseId}
                  to={`/post/${post.baseId}`}
                  className="sidebar-nav-link group"
                  title={post.originalIdea}
                >
                  <Icon name="FileText" size={16} className="flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground group-hover:text-foreground truncate">
                      {post.originalIdea?.substring(0, 40) || 'Untitled'}
                    </p>
                    <p className="text-[10px] text-muted-foreground/60">{post.publishedAt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </nav>

        {/* Credits / Usage Display */}
        {user && !isCollapsed && subscription && (
          <div className="px-3 py-3 mx-2 mb-2 bg-muted/40 rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {isPro ? 'Pro Plan' : 'Basic Plan'}
              </span>
              {!isPro && (
                <Link to="/pricing" className="text-[10px] font-medium text-primary hover:underline">
                  Upgrade
                </Link>
              )}
            </div>
            {!isPro && (
              <div className="space-y-1.5">
                {(() => {
                  const genUsed = subscription?.usage?.ai_generations ?? 0;
                  const genLimit = subscription?.limits?.ai_generations_per_month ?? 5;
                  const genPct = genLimit > 0 ? Math.min((genUsed / genLimit) * 100, 100) : 0;
                  return (
                    <div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                        <span>Generations</span>
                        <span>{genUsed}/{genLimit}</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${genPct >= 100 ? 'bg-red-500' : genPct >= 80 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${genPct}%` }} />
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const pubUsed = subscription?.usage?.publishes ?? 0;
                  const pubLimit = subscription?.limits?.publishes_per_month ?? 3;
                  const pubPct = pubLimit > 0 ? Math.min((pubUsed / pubLimit) * 100, 100) : 0;
                  return (
                    <div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                        <span>Publishes</span>
                        <span>{pubUsed}/{pubLimit}</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pubPct >= 100 ? 'bg-red-500' : pubPct >= 80 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pubPct}%` }} />
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const schUsed = subscription?.usage?.scheduled_posts ?? 0;
                  const schLimit = subscription?.limits?.scheduled_posts_per_month ?? 1;
                  const schPct = schLimit > 0 ? Math.min((schUsed / schLimit) * 100, 100) : 0;
                  return (
                    <div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                        <span>Scheduled</span>
                        <span>{schUsed}/{schLimit}</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${schPct >= 100 ? 'bg-red-500' : schPct >= 80 ? 'bg-amber-500' : 'bg-[#A78BFA]'}`} style={{ width: `${schPct}%` }} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            {isPro && (
              <p className="text-[10px] text-muted-foreground">Unlimited generations, publishes & scheduling</p>
            )}
          </div>
        )}
        {user && isCollapsed && !isPro && subscription && (
          <div className="flex justify-center mb-2">
            <Link to="/pricing" title="View usage & upgrade" className="w-8 h-8 rounded-lg bg-muted/40 border border-border flex items-center justify-center hover:bg-muted transition-colors">
              <Icon name="Zap" size={14} className="text-primary" />
            </Link>
          </div>
        )}

        <div className="sidebar-footer">
          {user && (
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className={`sidebar-nav-link w-full ${isActive('/profile') ? 'active' : ''}`}
                title={isCollapsed ? 'Profile' : ''}
              >
                <div className="flex items-center gap-3 w-full">
                  {userProfile?.avatar_url ? (
                    <img
                      src={userProfile?.avatar_url}
                      alt={userProfile?.full_name || 'User'}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7E6BC4, #A78BFA)' }}>
                      <Icon name="User" size={16} color="#FFFFFF" />
                    </div>
                  )}
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-foreground truncate">
                        {userProfile?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  )}
                  {!isCollapsed && (
                    <Icon name={showProfileMenu ? 'ChevronDown' : 'ChevronUp'} size={16} className="text-muted-foreground" />
                  )}
                </div>
              </button>

              {showProfileMenu && (
                <div className={`absolute ${isCollapsed ? 'left-full ml-2 bottom-0' : 'bottom-full left-0 right-0 mb-1'} bg-card border border-border rounded-lg shadow-lg py-1 z-50`}>
                  <button
                    onClick={() => { toggleTheme(); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={16} />
                    <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile'); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Settings" size={16} />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => { navigate('/feedback'); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="MessageSquareHeart" size={16} />
                    <span>Feedback</span>
                  </button>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { handleSignOut(); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
      {!isCollapsed && (
        <div className="sidebar-overlay" onClick={() => setIsCollapsed(true)} />
      )}
    </>
  );
};

export default Sidebar;
